import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, skip, switchMap } from 'rxjs/operators';
import { MailWorkflowService, MailTaskDto } from './mail-workflow.service';

@Component({
  selector: 'app-mail-workflow-list',
  template: `
    <div class="mwl">

      <!-- Header -->
      <div class="mwl__header">
        <div class="mwl__header-left">
          <span class="mwl__title">{{ title }}</span>
          <span class="mwl__subtitle">{{ subtitleText }}</span>
        </div>
        <div class="mwl__header-right">
          <!-- New Mail / Memo button — shown on DISPATCH, INBOX, and initiated lists -->
          <button class="mwl__new-btn"
                  *ngIf="showNewButton"
                  (click)="openNewMail()"
                  title="Start a new mail workflow">
            <span class="material-icons">add</span>
            New Mail / Memo
          </button>
          <button class="mwl__refresh" (click)="loadTasks()" title="Refresh">
            <span class="material-icons" [class.mwl__spinning]="loading">refresh</span>
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div class="mwl__loading" *ngIf="loading">
        <div class="mwl__spinner"></div>
        <span>Loading…</span>
      </div>

      <!-- Empty -->
      <div class="mwl__empty" *ngIf="!loading && tasks.length === 0">
        <span class="material-icons">mail_outline</span>
        <p>{{ emptyText }}</p>
        <button class="mwl__new-btn mwl__new-btn--centered"
                *ngIf="showNewButton"
                (click)="openNewMail()">
          <span class="material-icons">add</span>
          Start New Mail
        </button>
      </div>

      <!-- Table -->
      <table class="mwl__table" *ngIf="!loading && tasks.length > 0">
        <thead>
          <tr>
            <th style="width:24px"></th>
            <th>REF NO.</th>
            <th>SUBJECT</th>
            <th>FROM</th>
            <th>CLASS</th>
            <th>PRIORITY</th>
            <th>{{ mode === 'archive' ? 'ARCHIVED' : (mode === 'forwarded' ? 'FORWARDED' : 'DUE DATE') }}</th>
            <th>STATUS</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let task of tasks"
              [class.mwl__row--overdue]="task.overdue && !isDraft(task)"
              [class.mwl__row--urgent]="task.priority === 'URGENT'"
              [class.mwl__row--draft]="isDraft(task)"
              [class.mwl__row--bringup]="task.currentStage === 'BRINGUP'"
              (click)="openTask(task)">

            <!-- Unread / bringup indicator -->
            <td class="mwl__td-indicator">
              <span *ngIf="task.currentStage === 'BRINGUP'" class="material-icons mwl__bringup-icon" title="On Bring-Up">schedule</span>
              <span *ngIf="isDraft(task)" class="mwl__draft-dot" title="Draft"></span>
            </td>

            <!-- Ref No -->
            <td class="mwl__td-ref">
              <span class="mwl__ref">{{ task.referenceNumber || '—' }}</span>
              <span class="mwl__type-badge mwl__type-badge--{{ task.mailType?.toLowerCase() }}">
                {{ mailTypeLabel(task.mailType) }}
              </span>
            </td>

            <!-- Subject -->
            <td class="mwl__td-subject">
              <span class="mwl__subject-text">{{ task.subject || '(No subject)' }}</span>
              <span *ngIf="task.isSplitTask" class="mwl__tag mwl__tag--split">SPLIT</span>
              <span *ngIf="isDraft(task)"    class="mwl__tag mwl__tag--draft">DRAFT</span>
            </td>

            <!-- From -->
            <td class="mwl__td-from">
              <span class="mwl__from-name">{{ task.fromName || task.fromOrganization || '—' }}</span>
              <span *ngIf="task.fromOrganization && task.fromName" class="mwl__from-org">{{ task.fromOrganization }}</span>
            </td>

            <!-- Class -->
            <td class="mwl__td-class">
              <span class="mwl__class-badge">{{ task.fileClassCode || '—' }}</span>
            </td>

            <!-- Priority -->
            <td class="mwl__td-priority">
              <span class="mwl__priority mwl__priority--{{ task.priority?.toLowerCase() }}">
                <span *ngIf="task.priority === 'URGENT'" class="material-icons">priority_high</span>
                <span *ngIf="task.priority === 'CONFIDENTIAL'" class="material-icons">lock</span>
                {{ task.priority || 'NORMAL' }}
              </span>
            </td>

            <!-- Date column (context-aware) -->
            <td class="mwl__td-date"
                [class.mwl__td-date--overdue]="task.overdue && !isDraft(task)"
                [class.mwl__td-date--bringup]="task.currentStage === 'BRINGUP'">
              <span *ngIf="task.overdue && !isDraft(task)" class="material-icons mwl__warn-icon">warning</span>
              <ng-container *ngIf="task.currentStage === 'BRINGUP'">Bring-Up set</ng-container>
              <ng-container *ngIf="task.currentStage !== 'BRINGUP'">
                {{ task.dueDate ? (task.dueDate | date:'d MMM yyyy') : '—' }}
              </ng-container>
            </td>

            <!-- Status badge -->
            <td class="mwl__td-status">
              <span class="mwl__badge"
                    [class.mwl__badge--draft]="isDraft(task)"
                    [class.mwl__badge--dispatch]="!isDraft(task) && task.currentStage === 'DISPATCH'"
                    [class.mwl__badge--inbox]="!isDraft(task) && task.currentStage === 'INBOX'"
                    [class.mwl__badge--bringup]="task.currentStage === 'BRINGUP'"
                    [class.mwl__badge--archive]="task.currentStage === 'ARCHIVED'"
                    [class.mwl__badge--overdue]="task.overdue && !isDraft(task)">
                <ng-container *ngIf="isDraft(task)">Draft</ng-container>
                <ng-container *ngIf="!isDraft(task) && task.overdue">Overdue</ng-container>
                <ng-container *ngIf="!isDraft(task) && !task.overdue">{{ stageLabel(task.currentStage) }}</ng-container>
              </span>
              <span *ngIf="task.pendingSplits > 0" class="mwl__split-count">{{ task.pendingSplits }} split(s)</span>
            </td>

            <!-- Action buttons -->
            <td class="mwl__td-action" (click)="$event.stopPropagation()">
              <!-- Delete draft button -->
              <button *ngIf="isDraft(task)"
                      class="mwl__action-btn mwl__action-btn--delete"
                      (click)="deleteDraft(task, $event)"
                      [disabled]="deletingId === task.taskId"
                      title="Delete draft">
                <span class="material-icons">delete_outline</span>
              </button>
              <!-- View button -->
              <button class="mwl__action-btn" (click)="openTask(task)" title="Open task">
                <span class="material-icons">{{ isDraft(task) ? 'edit' : 'visibility' }}</span>
              </button>
            </td>

          </tr>
        </tbody>
      </table>

    </div>
  `,
  styles: [`
    .mwl { background: #fff; border-radius: 8px; overflow: hidden; }

    /* Header */
    .mwl__header {
      display: flex; align-items: center; justify-content: space-between;
      padding: 14px 20px 10px; border-bottom: 1px solid #e8edf5;
    }
    .mwl__header-left { display: flex; flex-direction: column; gap: 2px; }
    .mwl__header-right { display: flex; align-items: center; gap: 8px; }
    .mwl__title { font-size: 14px; font-weight: 700; color: #1B3A6B; }
    .mwl__subtitle { font-size: 11px; color: #7a8fa6; }

    /* New Mail button */
    .mwl__new-btn {
      display: inline-flex; align-items: center; gap: 5px;
      background: #0D7A5F; color: white;
      border: none; border-radius: 6px;
      padding: 7px 14px; font-size: 12px; font-weight: 600;
      cursor: pointer; transition: background 0.15s;
    }
    .mwl__new-btn:hover { background: #0a6349; }
    .mwl__new-btn .material-icons { font-size: 15px; }
    .mwl__new-btn--centered { margin-top: 12px; }

    /* Refresh */
    .mwl__refresh {
      background: none; border: none; cursor: pointer;
      color: #7a8fa6; padding: 4px; border-radius: 4px;
      display: flex; align-items: center;
    }
    .mwl__refresh:hover { color: #1B3A6B; background: #f0f4fb; }
    .mwl__spinning { animation: mwl-spin 1s linear infinite; }
    @keyframes mwl-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

    /* Loading */
    .mwl__loading {
      display: flex; align-items: center; gap: 10px;
      padding: 24px 20px; color: #7a8fa6; font-size: 13px;
    }
    .mwl__spinner {
      width: 20px; height: 20px; border: 2px solid #e0e8f4;
      border-top-color: #4A90D9; border-radius: 50%;
      animation: mwl-spin 0.8s linear infinite;
    }

    /* Empty */
    .mwl__empty {
      display: flex; flex-direction: column; align-items: center;
      padding: 40px 20px; color: #aab5c5; text-align: center;
    }
    .mwl__empty .material-icons { font-size: 40px; color: #d0daea; margin-bottom: 8px; }
    .mwl__empty p { font-size: 13px; margin: 0; }

    /* Table */
    .mwl__table { width: 100%; border-collapse: collapse; font-size: 13px; }
    .mwl__table thead tr { background: #f4f7fb; border-bottom: 2px solid #e0e8f4; }
    .mwl__table th {
      padding: 10px 14px; text-align: left;
      font-size: 11px; font-weight: 700; color: #7a8fa6;
      letter-spacing: 0.4px; white-space: nowrap;
    }
    .mwl__table tbody tr {
      border-bottom: 1px solid #f0f4fa;
      cursor: pointer; transition: background 0.12s;
    }
    .mwl__table tbody tr:hover { background: #f8fafd; }
    .mwl__table td { padding: 11px 14px; vertical-align: middle; color: #2d3748; }

    .mwl__row--overdue { background: #fff9f9 !important; }
    .mwl__row--urgent  { border-left: 3px solid #dc3545 !important; }
    .mwl__row--draft   { background: #fafbff !important; }
    .mwl__row--bringup { opacity: 0.75; }

    /* Indicator column */
    .mwl__td-indicator { width: 24px; padding: 0 4px 0 14px !important; }
    .mwl__bringup-icon { font-size: 16px !important; color: #2B5CB8; }
    .mwl__draft-dot {
      display: inline-block; width: 8px; height: 8px;
      border-radius: 50%; background: #8a9bb0;
    }

    /* Ref */
    .mwl__td-ref { white-space: nowrap; }
    .mwl__ref { display: block; font-weight: 700; color: #4A90D9; font-family: monospace; font-size: 12px; }
    .mwl__type-badge {
      display: inline-block; margin-top: 3px;
      padding: 1px 6px; border-radius: 10px; font-size: 10px; font-weight: 600;
    }
    .mwl__type-badge--incoming     { background: #EBF3FB; color: #1B3A6B; }
    .mwl__type-badge--outgoing     { background: #d4edda; color: #155724; }
    .mwl__type-badge--internal_memo{ background: #fff3cd; color: #856404; }

    /* Subject */
    .mwl__td-subject { max-width: 260px; }
    .mwl__subject-text {
      display: block; white-space: nowrap; overflow: hidden;
      text-overflow: ellipsis; font-weight: 500; color: #1a1a2e;
    }
    .mwl__tag {
      display: inline-block; margin-top: 2px;
      padding: 1px 5px; font-size: 9px; font-weight: 700;
      border-radius: 3px;
    }
    .mwl__tag--split { background: #e9ecef; color: #495057; }
    .mwl__tag--draft { background: #f0f4fa; color: #6b7280; border: 1px solid #d8e4f0; }

    /* From */
    .mwl__td-from { max-width: 160px; }
    .mwl__from-name { display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .mwl__from-org { display: block; font-size: 11px; color: #7a8fa6; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

    /* Class */
    .mwl__class-badge {
      display: inline-block; padding: 3px 8px;
      background: #f0f4fb; color: #1B3A6B;
      border-radius: 4px; font-size: 11px; font-weight: 700;
    }

    /* Priority */
    .mwl__priority {
      display: inline-flex; align-items: center; gap: 3px;
      font-size: 11px; font-weight: 600;
    }
    .mwl__priority .material-icons { font-size: 13px; }
    .mwl__priority--normal      { color: #6c757d; }
    .mwl__priority--urgent      { color: #dc3545; }
    .mwl__priority--confidential{ color: #856404; }

    /* Date */
    .mwl__td-date { white-space: nowrap; color: #555; font-size: 12px; }
    .mwl__td-date--overdue { color: #dc3545; font-weight: 600; }
    .mwl__td-date--bringup { color: #2B5CB8; font-style: italic; }
    .mwl__warn-icon { font-size: 13px !important; vertical-align: middle; margin-right: 3px; color: #dc3545; }

    /* Status badges */
    .mwl__td-status { white-space: nowrap; }
    .mwl__badge {
      display: inline-block; padding: 3px 10px;
      border-radius: 20px; font-size: 11px; font-weight: 600;
    }
    .mwl__badge--draft    { background: #f0f4fa; color: #6b7280; border: 1px solid #d8e4f0; }
    .mwl__badge--dispatch { background: #fff3cd; color: #856404; }
    .mwl__badge--inbox    { background: #EBF3FB; color: #1B3A6B; }
    .mwl__badge--bringup  { background: rgba(43,92,184,0.1); color: #2B5CB8; }
    .mwl__badge--archive  { background: #f0f0f0; color: #6b7280; }
    .mwl__badge--overdue  { background: #f8d7da; color: #721c24; }
    .mwl__split-count { display: block; font-size: 10px; color: #7a8fa6; margin-top: 2px; }

    /* Action */
    .mwl__td-action { text-align: right; white-space: nowrap; width: 80px; }
    .mwl__action-btn {
      background: none; border: 1px solid #e0e8f4;
      border-radius: 6px; padding: 5px 7px; cursor: pointer;
      color: #4A90D9; display: inline-flex; align-items: center;
      transition: all .12s; margin-left: 4px;
    }
    .mwl__action-btn:hover { background: #4A90D9; color: #fff; border-color: #4A90D9; }
    .mwl__action-btn .material-icons { font-size: 15px; }
    .mwl__action-btn--delete { color: #dc3545; border-color: #f8d7da; }
    .mwl__action-btn--delete:hover { background: #dc3545; color: #fff; border-color: #dc3545; }
    .mwl__action-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  `]
})
export class MailWorkflowListComponent implements OnInit, OnChanges, OnDestroy {

  @Input() taskType: 'DISPATCH' | 'INBOX' | null = 'INBOX';
  @Input() title = 'Mail Tasks';
  @Input() mode: 'assigned' | 'initiated' | 'forwarded' | 'bringup' | 'archive' | 'drafts' = 'assigned';

  tasks: MailTaskDto[] = [];
  loading = false;
  deletingId: string | null = null;

  private destroy$ = new Subject<void>();
  private reload$  = new Subject<void>();

  // ── Computed ─────────────────────────────────────────────────────────────

  get showNewButton(): boolean {
    return ['assigned', 'initiated'].includes(this.mode);
  }

  get subtitleText(): string {
    switch (this.mode) {
      case 'initiated':  return 'Workflows you submitted (including drafts)';
      case 'forwarded':  return 'Items you have forwarded';
      case 'bringup':    return 'Items on hold — bring-up active';
      case 'archive':    return 'Completed and archived mail items';
      default:
        return { DISPATCH: 'Mail items awaiting dispatch', INBOX: 'Items in your inbox' }[this.taskType ?? ''] ?? '';
    }
  }

  get emptyText(): string {
    switch (this.mode) {
      case 'initiated':  return 'No submitted mail workflows yet. Start one with the button above.';
      case 'forwarded':  return 'You have not forwarded any mail items.';
      case 'bringup':    return 'No items on bring-up.';
      case 'archive':    return 'No archived items found.';
      default:
        return `No pending ${{ DISPATCH: 'dispatch', INBOX: 'inbox' }[this.taskType ?? ''] ?? ''} items.`;
    }
  }

  isDraft(task: MailTaskDto): boolean {
    return task.status === 'DRAFT' || task.currentStage === 'DRAFT' || task.taskType === 'DRAFT';
  }

  mailTypeLabel(type: string): string {
    return { INCOMING: 'INC', OUTGOING: 'OUT', INTERNAL_MEMO: 'MEMO' }[type] ?? type;
  }

  stageLabel(stage: string): string {
    return {
      DISPATCH: 'In Dispatch', INBOX: 'In Inbox',
      BRINGUP: 'Bring-Up', ARCHIVED: 'Archived',
    }[stage] ?? stage;
  }

  constructor(
    private mailService: MailWorkflowService,
    private router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['taskType'] || changes['mode']) && !changes['taskType']?.firstChange) {
      this.reload$.next();
    }
  }

  ngOnInit(): void {
    this.reload$.pipe(
      takeUntil(this.destroy$),
      switchMap(() => {
        this.loading = true;
        return this.fetchTasks();
      })
    ).subscribe({
      next:  tasks => { this.tasks = tasks; this.loading = false; },
      error: err   => { console.error('Failed to load mail tasks', err); this.loading = false; }
    });

    this.reload$.next();

    this.mailService.taskCounts$.pipe(skip(1), takeUntil(this.destroy$))
      .subscribe(() => this.reload$.next());
  }

  ngOnDestroy(): void {
    this.destroy$.next(); this.destroy$.complete();
    this.reload$.complete();
  }

  private fetchTasks() {
    switch (this.mode) {
      case 'initiated': return this.mailService.getInitiatedWorkflows();
      case 'forwarded': return this.mailService.getForwardedItems();
      case 'bringup':   return this.mailService.getBringupItems();
      case 'archive':   return this.mailService.getArchivedItems();
      default:          return this.mailService.getMyTasks(this.taskType ?? undefined);
    }
  }

  loadTasks(): void { this.reload$.next(); }

  openNewMail(): void {
    this.router.navigate(['/mail-workflow/new']);
  }

  openTask(task: MailTaskDto): void {
    if (this.isDraft(task)) {
      // Draft — open wizard pre-filled at step 6
      this.router.navigate(['/mail-workflow/draft', task.taskId], {
        state: { draft: task }
      });
    } else if (!task.taskId || task.currentStage === 'ARCHIVED') {
      // Archived/completed — no active Flowable task exists.
      // Navigate using documentNodeId; the detail component and backend
      // will serve a readonly view.
      this.router.navigate(['/mail-workflow/task', task.documentNodeId]);
    } else {
      // Active task — navigate normally using Flowable taskId
      this.router.navigate(['/mail-workflow/task', task.taskId]);
    }
  }

  deleteDraft(task: MailTaskDto, event: Event): void {
    event.stopPropagation();
    if (!confirm(`Delete draft "${task.subject || task.referenceNumber || 'this item'}"?`)) return;
    this.deletingId = task.taskId;
    this.mailService.deleteDraft(task.taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.deletingId = null;
          this.tasks = this.tasks.filter(t => t.taskId !== task.taskId);
        },
        error: () => { this.deletingId = null; }
      });
  }
}